import { useState, useCallback, useEffect, useRef } from "react";
import { Autocomplete, Box, CircularProgress, IconButton, Stack, TextField, Tooltip } from "@mui/material";
import MyLocationRoundedIcon from "@mui/icons-material/MyLocationRounded";
import { useQuery } from "@tanstack/react-query";
import {
  searchAddressSuggestions,
  fetchPlaceDetails,
  reverseGeocodeFromPosition,
  type AddressAutocompleteSuggestion,
} from "@/services/address.service";

function useDebounce<T>(value: T, delayMs: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedValue(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debouncedValue;
}

function createSessionToken(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export interface AddressAutocompleteProps {
  /** Valeur adresse (ligne principale). */
  address: string;
  /** Code postal (affiché / pré-rempli). */
  postalCode?: string;
  /** Ville (affichée / pré-remplie). */
  city?: string;
  /** Callback quand l'utilisateur sélectionne une suggestion ou modifie le texte. */
  onAddressChange: (payload: {
    address: string;
    postalCode?: string | null;
    city?: string | null;
    countryCode?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  }) => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  multiline?: boolean;
  minChars?: number;
  /** Bouton « ma position » à droite du champ (géolocalisation + géocodage inverse). */
  showLocateButton?: boolean;
  /** Erreur affichée sous le champ (ex. refus géolocalisation). */
  onLocateError?: (message: string) => void;
}

const DEBOUNCE_MS = 400;
const MIN_CHARS_DEFAULT = 3;

/**
 * Champ de saisie d'adresse avec suggestions (Google Places Autocomplete, biais Guyane).
 * À la sélection, récupère Place Details pour remplir address, postalCode et city.
 * En saisie libre, met à jour uniquement address.
 */
export function AddressAutocomplete({
  address,
  onAddressChange,
  label = "Adresse",
  placeholder,
  error,
  helperText,
  required,
  size = "small",
  fullWidth = true,
  multiline = false,
  minChars = MIN_CHARS_DEFAULT,
  showLocateButton = false,
  onLocateError,
}: AddressAutocompleteProps): JSX.Element {
  const [focused, setFocused] = useState(false);
  const [isResolvingDetails, setIsResolvingDetails] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const sessionTokenRef = useRef<string | null>(null);

  const ensureSessionToken = useCallback((): string => {
    if (sessionTokenRef.current) {
      return sessionTokenRef.current;
    }
    const token = createSessionToken();
    sessionTokenRef.current = token;
    setSessionToken(token);
    return token;
  }, []);

  const resetSessionToken = useCallback(() => {
    sessionTokenRef.current = null;
    setSessionToken(null);
  }, []);

  /** Ligne d'adresse uniquement (CP/ville ont leurs propres champs sur les formulaires). */
  const displayValue = address;
  const debouncedSearch = useDebounce(displayValue, DEBOUNCE_MS);

  const { data: options = [], isFetching } = useQuery({
    queryKey: ["address-autocomplete", debouncedSearch, sessionToken],
    queryFn: () =>
      searchAddressSuggestions(debouncedSearch, 8, sessionToken ?? undefined),
    enabled: focused && debouncedSearch.length >= minChars && !!sessionToken,
    staleTime: 60_000,
  });

  const showDropdown =
    focused &&
    displayValue.length >= minChars &&
    (isFetching || isResolvingDetails || options.length > 0);

  const handleInputChange = useCallback(
    (_: unknown, value: string) => {
      onAddressChange({ address: value });
    },
    [onAddressChange]
  );

  const handleChange = useCallback(
    async (_: unknown, value: string | AddressAutocompleteSuggestion | null) => {
      if (value == null) {
        onAddressChange({ address: "", postalCode: null, city: null, countryCode: null, latitude: null, longitude: null });
        resetSessionToken();
        setFocused(false);
        return;
      }
      if (typeof value === "string") {
        onAddressChange({ address: value, postalCode: null, city: null, countryCode: null, latitude: null, longitude: null });
        return;
      }

      const token = ensureSessionToken();
      setIsResolvingDetails(true);
      setFocused(false);
      try {
        const details = await fetchPlaceDetails(value.placeId, token);
        if (details) {
          onAddressChange({
            address: details.address,
            postalCode: details.postalCode ?? null,
            city: details.city ?? null,
            countryCode: details.countryCode ?? null,
            latitude: details.latitude,
            longitude: details.longitude,
          });
        } else {
          onAddressChange({ address: value.label, postalCode: null, city: null, countryCode: null, latitude: null, longitude: null });
        }
      } finally {
        setIsResolvingDetails(false);
        resetSessionToken();
      }
    },
    [onAddressChange, ensureSessionToken, resetSessionToken]
  );

  const loading = isFetching || isResolvingDetails || isLocating;

  const handleLocate = useCallback(async (): Promise<void> => {
    if (!navigator.geolocation) {
      onLocateError?.("La géolocalisation n'est pas disponible sur cet appareil.");
      return;
    }

    setIsLocating(true);
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15_000,
          maximumAge: 60_000,
        });
      });

      const details = await reverseGeocodeFromPosition(
        position.coords.latitude,
        position.coords.longitude,
      );
      if (!details) {
        onLocateError?.("Impossible de déterminer l'adresse à partir de votre position.");
        return;
      }

      resetSessionToken();
      setFocused(false);
      onAddressChange({
        address: details.address,
        postalCode: details.postalCode ?? null,
        city: details.city ?? null,
        countryCode: details.countryCode ?? null,
        latitude: details.latitude,
        longitude: details.longitude,
      });
    } catch (err) {
      const geoErr = err as GeolocationPositionError;
      if (geoErr?.code === 1) {
        onLocateError?.("Autorisez l'accès à votre position dans les paramètres du navigateur.");
      } else if (geoErr?.code === 3) {
        onLocateError?.("Délai dépassé lors de la localisation. Réessayez.");
      } else {
        onLocateError?.("Impossible d'obtenir votre position.");
      }
    } finally {
      setIsLocating(false);
    }
  }, [onAddressChange, onLocateError, resetSessionToken]);

  const autocompleteField = (
    <Autocomplete
      freeSolo
      open={showDropdown}
      value={displayValue || null}
      inputValue={displayValue}
      onInputChange={(_, v) => handleInputChange(_, v)}
      onChange={(_, v) => {
        void handleChange(_, v);
      }}
      options={options}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.label
      }
      isOptionEqualToValue={(a, b) =>
        typeof a !== "string" &&
        typeof b !== "string" &&
        a.placeId === b.placeId
      }
      loading={loading}
      fullWidth={fullWidth}
      size={size}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          required={required}
          multiline={multiline}
          rows={multiline ? 2 : undefined}
          onFocus={() => {
            if (!sessionTokenRef.current) {
              ensureSessionToken();
            }
            setFocused(true);
          }}
          onBlur={() => setTimeout(() => setFocused(false), 200)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );

  if (!showLocateButton) {
    return autocompleteField;
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ width: fullWidth ? "100%" : undefined }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>{autocompleteField}</Box>
      <Tooltip title="Utiliser ma position actuelle">
        <span>
          <IconButton
            type="button"
            onClick={() => void handleLocate()}
            disabled={loading}
            aria-label="Utiliser ma position actuelle"
            size={size}
            sx={{ mt: size === "small" ? 0.25 : 0.5 }}
          >
            {isLocating ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              <MyLocationRoundedIcon />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}
